import { useEffect, useState, useCallback } from 'react';
import useConfiguracionAdmin from '../../hooks/useConfiguracionAdmin';
import { recortarLogoCentrado } from '../../utils/recortarLogoInstitucional';
import { DISTRITOS } from '../../utils/distritos';
import MapaSelectorCoordenadas from '../../components/Panel/MapaSelectorCoordenadas';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

// Pestañas de la pantalla de Configuración (HU-029) — para no tener todas las
// tarjetas apiladas. Cada tarjeta lleva `data-tab` y el CSS muestra solo la del
// tab activo.
const TABS_CONFIG = [
  { id: 'identidad', label: 'Identidad' },
  { id: 'mapa', label: 'Mapa' },
  { id: 'encabezado', label: 'Encabezado' },
  { id: 'inicio', label: 'Inicio' },
  { id: 'reporte', label: 'Reporte' },
  { id: 'comentarios', label: 'Comentarios' },
  { id: 'politicas', label: 'Políticas' },
  { id: 'marca', label: 'Marca de agua' },
];

// Pasos del formulario de reporte cuyos textos/color se pueden personalizar
// (HU-026). `titulo`/`subtitulo` son el texto por defecto del componente.
const PASOS_REPORTE = [
  { clave: 'categoria', etiqueta: 'Paso 1 — Categoría', titulo: '¿Qué pasó?', subtitulo: 'Elige la categoría que mejor describe lo que viste.' },
  { clave: 'datos', etiqueta: 'Paso 2 — Datos', titulo: '¿Cómo deseas reportar?', subtitulo: 'Elige una opción para continuar.' },
  { clave: 'fecha', etiqueta: 'Paso 3 — Fecha y hora', titulo: '¿Cuándo ocurrió?', subtitulo: '' },
  { clave: 'evidencia', etiqueta: 'Paso 5 — Evidencia', titulo: 'Evidencia', subtitulo: '' },
  { clave: 'resumen', etiqueta: 'Resumen', titulo: 'Resumen', subtitulo: 'Revisa antes de enviar — puedes tocar cualquier tarjeta para corregirla.' },
];

// Selector de color reutilizable (paleta + hex editable).
const SelectorColor = ({ valor, onCambiar }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <input
      type="color"
      style={{ width: 46, height: 40, padding: 2, border: '1px solid var(--hairline, #ddd)', borderRadius: 10, background: 'none', cursor: 'pointer', flexShrink: 0 }}
      value={valor}
      onChange={(e) => onCambiar(e.target.value)}
    />
    <input className="panel-input" style={{ flex: 1 }} value={valor} onChange={(e) => onCambiar(e.target.value)} />
  </div>
);

/**
 * Configuración de esta instancia (HU-005 Fase 4, rediseño fiel a
 * design_handoff_reportes_ciudadanos sección 7.7; HU-009-D: la tarjeta de
 * Categorías se mudó a su propia pantalla del grupo "Operaciones",
 * `GestionCategorias.jsx` — acá quedan solo 3 tarjetas: Identidad, Mapa y
 * Marca de agua, todo lo que es específicamente configuración de la
 * instancia). Exclusiva de `ADMINISTRADOR`/`SUPERADMIN` (gateado por
 * `RutaProtegidaPanel`, no acá).
 */
const ConfiguracionInstancia = () => {
  const { loading: guardandoConfig, error: errorConfig, obtenerConfiguracion, actualizarConfiguracion } = useConfiguracionAdmin();

  const [config, setConfig] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [encabezadoPreview, setEncabezadoPreview] = useState(null);
  const [inicioPreview, setInicioPreview] = useState(null);
  const [inicioFondoPreview, setInicioFondoPreview] = useState(null);
  const [ogImagenPreview, setOgImagenPreview] = useState(null);
  const [ogImagen2Preview, setOgImagen2Preview] = useState(null);
  const [featuresPreview, setFeaturesPreview] = useState(null);
  const [mensajeConfig, setMensajeConfig] = useState('');
  const [tabConfig, setTabConfig] = useState('identidad');

  const cargarConfig = useCallback(async () => {
    const resultado = await obtenerConfiguracion();
    if (resultado.success) setConfig(resultado.configuracion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { cargarConfig(); }, [cargarConfig]);

  const guardarConfig = async (evento) => {
    evento.preventDefault();
    setMensajeConfig('');
    const resultado = await actualizarConfiguracion(config);
    if (resultado.success) {
      setConfig(resultado.configuracion);
      setLogoPreview(null);
      setBannerPreview(null);
      setEncabezadoPreview(null);
      setInicioPreview(null);
      setInicioFondoPreview(null);
      setOgImagenPreview(null);
      setMensajeConfig('Configuración guardada.');
    } else {
      setMensajeConfig(resultado.error);
    }
  };

  const handleLogoInstancia = async (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensajeConfig('');
    try {
      const logoRecortado = await recortarLogoCentrado(archivo);
      setConfig({ ...config, logo: logoRecortado });
      setLogoPreview(URL.createObjectURL(logoRecortado));
    } catch (err) {
      setMensajeConfig(err.message);
    }
  };

  // HU-015: a diferencia del logo, el banner es panorámico — se sube tal
  // cual, sin recorte cuadrado (los componentes de campaña de HU-016 lo
  // adaptan a cada pantalla).
  const handleBannerInstancia = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensajeConfig('');
    setConfig({ ...config, banner: archivo });
    setBannerPreview(URL.createObjectURL(archivo));
  };

  const quitarBanner = () => {
    setConfig({ ...config, banner: null });
    setBannerPreview(null);
  };

  // HU-017: encabezado sobre el buscador del mapa — puede ser solo texto
  // (título + subtítulo) o incluir una imagen panorámica. Igual que el banner
  // de campaña, la imagen se sube tal cual (sin recorte).
  const handleEncabezadoImagen = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensajeConfig('');
    setConfig({ ...config, encabezado_imagen: archivo });
    setEncabezadoPreview(URL.createObjectURL(archivo));
  };

  const quitarEncabezadoImagen = () => {
    setConfig({ ...config, encabezado_imagen: null });
    setEncabezadoPreview(null);
  };

  // HU-018: imagen de la pantalla de inicio (splash).
  const handleInicioImagen = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensajeConfig('');
    setConfig({ ...config, inicio_imagen: archivo });
    setInicioPreview(URL.createObjectURL(archivo));
  };

  const quitarInicioImagen = () => {
    setConfig({ ...config, inicio_imagen: null });
    setInicioPreview(null);
  };

  // HU-018: imagen de FONDO del splash (cubre la pantalla), con su interruptor.
  const handleInicioFondoImagen = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensajeConfig('');
    setConfig({ ...config, inicio_fondo_imagen: archivo });
    setInicioFondoPreview(URL.createObjectURL(archivo));
  };

  const quitarInicioFondoImagen = () => {
    setConfig({ ...config, inicio_fondo_imagen: null });
    setInicioFondoPreview(null);
  };

  // HU-032: imagen de la vista previa del enlace (Open Graph).
  const handleOgImagen = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensajeConfig('');
    setConfig({ ...config, og_imagen: archivo });
    setOgImagenPreview(URL.createObjectURL(archivo));
  };

  const quitarOgImagen = () => {
    setConfig({ ...config, og_imagen: null });
    setOgImagenPreview(null);
  };

  // Segunda imagen de la vista previa del enlace (og:image adicional).
  const handleOgImagen2 = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensajeConfig('');
    setConfig({ ...config, og_imagen_2: archivo });
    setOgImagen2Preview(URL.createObjectURL(archivo));
  };

  const quitarOgImagen2 = () => {
    setConfig({ ...config, og_imagen_2: null });
    setOgImagen2Preview(null);
  };

  // Imagen propia que reemplaza los 3 íconos del splash (Seguro/Rápido/Confiable).
  const handleFeaturesImagen = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensajeConfig('');
    setConfig({ ...config, inicio_features_imagen: archivo });
    setFeaturesPreview(URL.createObjectURL(archivo));
  };

  const quitarFeaturesImagen = () => {
    setConfig({ ...config, inicio_features_imagen: null });
    setFeaturesPreview(null);
  };

  if (!config) {
    return (
      <PanelShell>
        <p className="panel-texto-ayuda">Cargando configuración…</p>
        {errorConfig && <p className="panel-gu-mensaje">{errorConfig}</p>}
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      <div className="panel-gu-cabecera">
        <h2 style={{ margin: 0 }}>Configuración</h2>
        <button type="submit" form="form-config-instancia" className="panel-boton-header-accion" disabled={guardandoConfig}>
          <i className="fas fa-floppy-disk"></i> {guardandoConfig ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>

      {mensajeConfig && <p className="panel-gu-mensaje">{mensajeConfig}</p>}

      <div className="panel-tabs">
        {TABS_CONFIG.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`panel-tab${tabConfig === t.id ? ' panel-tab-activo' : ''}`}
            onClick={() => setTabConfig(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form id="form-config-instancia" className="panel-config-grid-2x2" data-tab-activo={tabConfig} onSubmit={guardarConfig}>
        <div className="panel-config-tarjeta" data-tab="identidad">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-building"></i> Identidad</span>

          <label className="panel-label">Nombre de la entidad</label>
          <input className="panel-input" required value={config.nombre_entidad}
            onChange={(e) => setConfig({ ...config, nombre_entidad: e.target.value })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Logo institucional</label>
          <div className="panel-config-logo-fila">
            <div className="panel-config-logo-preview-caja">
              {(logoPreview || config.logo)
                ? <img src={logoPreview || config.logo} alt="Logo actual" />
                : <span>{config.nombre_entidad?.[0] || 'C'}</span>}
            </div>
            <label className="panel-boton-secundario panel-gu-label-archivo">
              Cambiar logo
              <input type="file" accept="image/png" onChange={handleLogoInstancia} hidden />
            </label>
          </div>
          <p className="panel-texto-ayuda">Solo PNG — se recorta automáticamente al cuadrado centrado.</p>

          <label className="panel-label" style={{ marginTop: 14 }}>Banner promocional (opcional)</label>
          <div className="panel-config-banner-preview-caja">
            {(bannerPreview || config.banner)
              ? <img src={bannerPreview || config.banner} alt="Banner actual" />
              : <span className="panel-texto-ayuda">Sin banner cargado</span>}
          </div>
          <div className="panel-gu-acciones" style={{ marginTop: 8 }}>
            <label className="panel-boton-secundario panel-gu-label-archivo">
              {(bannerPreview || config.banner) ? 'Cambiar banner' : 'Subir banner'}
              <input type="file" accept="image/png,image/jpeg" onChange={handleBannerInstancia} hidden />
            </label>
            {(bannerPreview || config.banner) && (
              <button type="button" className="panel-boton-secundario" onClick={quitarBanner}>Quitar banner</button>
            )}
          </div>
          <p className="panel-texto-ayuda">
            Se muestra animado en el login, el panel principal y el mapa ciudadano — sin banner
            cargado, esas pantallas quedan igual que hoy.
          </p>

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={config.campana_cta_activo !== false}
              onChange={(e) => setConfig({ ...config, campana_cta_activo: e.target.checked })} />
            Mostrar botón 1
          </label>
          <label className="panel-label" style={{ marginTop: 8 }}>Botón 1 — texto</label>
          <input className="panel-input" maxLength={60} placeholder="Ej. Ver propuestas"
            value={config.campana_cta_texto || ''}
            onChange={(e) => setConfig({ ...config, campana_cta_texto: e.target.value })} />
          <label className="panel-label" style={{ marginTop: 14 }}>Botón 1 — URL</label>
          <input className="panel-input" placeholder="https://…"
            value={config.campana_cta_url || ''}
            onChange={(e) => setConfig({ ...config, campana_cta_url: e.target.value })} />
          <label className="panel-config-checkbox-row" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={!!config.campana_cta_modal}
              onChange={(e) => setConfig({ ...config, campana_cta_modal: e.target.checked })} />
            Botón 1 — abrir en modal
          </label>

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={!!config.campana_cta2_activo}
              onChange={(e) => setConfig({ ...config, campana_cta2_activo: e.target.checked })} />
            Mostrar botón 2
          </label>
          <label className="panel-label" style={{ marginTop: 8 }}>Botón 2 — texto</label>
          <input className="panel-input" maxLength={60} placeholder="Ej. Ver obras"
            value={config.campana_cta2_texto || ''}
            onChange={(e) => setConfig({ ...config, campana_cta2_texto: e.target.value })} />
          <label className="panel-label" style={{ marginTop: 14 }}>Botón 2 — URL</label>
          <input className="panel-input" placeholder="https://…"
            value={config.campana_cta2_url || ''}
            onChange={(e) => setConfig({ ...config, campana_cta2_url: e.target.value })} />
          <label className="panel-config-checkbox-row" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={!!config.campana_cta2_modal}
              onChange={(e) => setConfig({ ...config, campana_cta2_modal: e.target.checked })} />
            Botón 2 — abrir en modal
          </label>

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={!!config.campana_cta3_activo}
              onChange={(e) => setConfig({ ...config, campana_cta3_activo: e.target.checked })} />
            Mostrar botón 3
          </label>
          <label className="panel-label" style={{ marginTop: 8 }}>Botón 3 — texto</label>
          <input className="panel-input" maxLength={60} placeholder="Ej. Mis obras"
            value={config.campana_cta3_texto || ''}
            onChange={(e) => setConfig({ ...config, campana_cta3_texto: e.target.value })} />
          <label className="panel-label" style={{ marginTop: 14 }}>Botón 3 — URL</label>
          <input className="panel-input" placeholder="https://…"
            value={config.campana_cta3_url || ''}
            onChange={(e) => setConfig({ ...config, campana_cta3_url: e.target.value })} />
          <label className="panel-config-checkbox-row" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={!!config.campana_cta3_modal}
              onChange={(e) => setConfig({ ...config, campana_cta3_modal: e.target.checked })} />
            Botón 3 — abrir en modal
          </label>

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={!!config.campana_cta4_activo}
              onChange={(e) => setConfig({ ...config, campana_cta4_activo: e.target.checked })} />
            Mostrar botón 4
          </label>
          <label className="panel-label" style={{ marginTop: 8 }}>Botón 4 — texto</label>
          <input className="panel-input" maxLength={60} placeholder="Ej. Propuestas"
            value={config.campana_cta4_texto || ''}
            onChange={(e) => setConfig({ ...config, campana_cta4_texto: e.target.value })} />
          <label className="panel-label" style={{ marginTop: 14 }}>Botón 4 — URL</label>
          <input className="panel-input" placeholder="https://…"
            value={config.campana_cta4_url || ''}
            onChange={(e) => setConfig({ ...config, campana_cta4_url: e.target.value })} />
          <label className="panel-config-checkbox-row" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={!!config.campana_cta4_modal}
              onChange={(e) => setConfig({ ...config, campana_cta4_modal: e.target.checked })} />
            Botón 4 — abrir en modal
          </label>

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={config.campana_pilares_activo !== false}
              onChange={(e) => setConfig({ ...config, campana_pilares_activo: e.target.checked })} />
            Mostrar pilares
          </label>

          <label className="panel-config-checkbox-row">
            <input type="checkbox" checked={config.campana_pilar_seguridad !== false}
              disabled={config.campana_pilares_activo === false}
              onChange={(e) => setConfig({ ...config, campana_pilar_seguridad: e.target.checked })} />
            Pilar 1 — visible
          </label>
          <input className="panel-input" maxLength={40} placeholder="Texto (ej. Seguridad)"
            value={config.campana_pilar_1_texto || ''}
            onChange={(e) => setConfig({ ...config, campana_pilar_1_texto: e.target.value })} />
          <input className="panel-input" style={{ marginTop: 6 }} placeholder="URL del pilar (opcional)"
            value={config.campana_pilar_1_url || ''}
            onChange={(e) => setConfig({ ...config, campana_pilar_1_url: e.target.value })} />

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={config.campana_pilar_orden !== false}
              disabled={config.campana_pilares_activo === false}
              onChange={(e) => setConfig({ ...config, campana_pilar_orden: e.target.checked })} />
            Pilar 2 — visible
          </label>
          <input className="panel-input" maxLength={40} placeholder="Texto (ej. Orden)"
            value={config.campana_pilar_2_texto || ''}
            onChange={(e) => setConfig({ ...config, campana_pilar_2_texto: e.target.value })} />
          <input className="panel-input" style={{ marginTop: 6 }} placeholder="URL del pilar (opcional)"
            value={config.campana_pilar_2_url || ''}
            onChange={(e) => setConfig({ ...config, campana_pilar_2_url: e.target.value })} />

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={config.campana_pilar_experiencia !== false}
              disabled={config.campana_pilares_activo === false}
              onChange={(e) => setConfig({ ...config, campana_pilar_experiencia: e.target.checked })} />
            Pilar 3 — visible
          </label>
          <input className="panel-input" maxLength={40} placeholder="Texto (ej. Experiencia)"
            value={config.campana_pilar_3_texto || ''}
            onChange={(e) => setConfig({ ...config, campana_pilar_3_texto: e.target.value })} />
          <input className="panel-input" style={{ marginTop: 6 }} placeholder="URL del pilar (opcional)"
            value={config.campana_pilar_3_url || ''}
            onChange={(e) => setConfig({ ...config, campana_pilar_3_url: e.target.value })} />
          <p className="panel-texto-ayuda">
            Si un pilar tiene URL, se vuelve clickeable (abre en pestaña nueva). Sin URL, solo se muestra.
          </p>

          <label className="panel-label" style={{ marginTop: 14 }}>Slogan de campaña (opcional)</label>
          <input className="panel-input" placeholder='Ej. "Lo bueno debe volver"' value={config.slogan || ''}
            onChange={(e) => setConfig({ ...config, slogan: e.target.value })} />

          <label className="panel-label" style={{ marginTop: 18 }}>Vista previa del enlace — título</label>
          <input className="panel-input" maxLength={150} placeholder="Ej. Avanza País — Reporta tu incidencia"
            value={config.og_titulo || ''}
            onChange={(e) => setConfig({ ...config, og_titulo: e.target.value })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Vista previa del enlace — descripción</label>
          <input className="panel-input" maxLength={220} placeholder="Ej. Registra y ubica las necesidades de tu barrio en el mapa."
            value={config.og_descripcion || ''}
            onChange={(e) => setConfig({ ...config, og_descripcion: e.target.value })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Vista previa del enlace — imagen (opcional)</label>
          <div className="panel-config-banner-preview-caja">
            {(ogImagenPreview || config.og_imagen)
              ? <img src={ogImagenPreview || config.og_imagen} alt="Vista previa del enlace" />
              : <span className="panel-texto-ayuda">Sin imagen — se usa el logo institucional</span>}
          </div>
          <div className="panel-gu-acciones" style={{ marginTop: 8 }}>
            <label className="panel-boton-secundario panel-gu-label-archivo">
              {(ogImagenPreview || config.og_imagen) ? 'Cambiar imagen' : 'Subir imagen'}
              <input type="file" accept="image/png,image/jpeg" onChange={handleOgImagen} hidden />
            </label>
            {(ogImagenPreview || config.og_imagen) && (
              <button type="button" className="panel-boton-secundario" onClick={quitarOgImagen}>Quitar imagen</button>
            )}
          </div>
          <p className="panel-texto-ayuda">
            Es lo que se ve al compartir el enlace (WhatsApp/redes). Se aplica cuando el frontend se reinicia.
          </p>

          <label className="panel-label" style={{ marginTop: 14 }}>Vista previa del enlace — 2ª imagen (opcional)</label>
          <div className="panel-config-banner-preview-caja">
            {(ogImagen2Preview || config.og_imagen_2)
              ? <img src={ogImagen2Preview || config.og_imagen_2} alt="Segunda imagen de la vista previa" />
              : <span className="panel-texto-ayuda">Sin segunda imagen</span>}
          </div>
          <div className="panel-gu-acciones" style={{ marginTop: 8 }}>
            <label className="panel-boton-secundario panel-gu-label-archivo">
              {(ogImagen2Preview || config.og_imagen_2) ? 'Cambiar 2ª imagen' : 'Subir 2ª imagen'}
              <input type="file" accept="image/png,image/jpeg" onChange={handleOgImagen2} hidden />
            </label>
            {(ogImagen2Preview || config.og_imagen_2) && (
              <button type="button" className="panel-boton-secundario" onClick={quitarOgImagen2}>Quitar 2ª imagen</button>
            )}
          </div>
        </div>

        <div className="panel-config-tarjeta" data-tab="mapa">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-map-location-dot"></i> Centro del mapa</span>

          <label className="panel-label">Distrito por defecto</label>
          <select className="panel-input" value={config.distrito_defecto || 'Jesús María'}
            onChange={(e) => setConfig({ ...config, distrito_defecto: e.target.value })}>
            {DISTRITOS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <p className="panel-texto-ayuda">El mapa público se carga centrado en este distrito.</p>

          <label className="panel-config-checkbox-row" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={!!config.solo_registrar_en_distrito}
              onChange={(e) => setConfig({ ...config, solo_registrar_en_distrito: e.target.checked })} />
            Solo permitir registrar dentro de ese distrito
          </label>
          <p className="panel-texto-ayuda">
            Si está activo, el ciudadano solo podrá marcar la ubicación dentro del distrito elegido.
          </p>

          <MapaSelectorCoordenadas
            lat={config.mapa_lat}
            lng={config.mapa_lng}
            distrito={config.distrito_defecto}
            onCambiar={(lat, lng) => setConfig({ ...config, mapa_lat: lat, mapa_lng: lng })}
          />
          <p className="panel-texto-ayuda">
            {Number(config.mapa_lat).toFixed(6)}, {Number(config.mapa_lng).toFixed(6)}
          </p>

          <label className="panel-label" style={{ marginTop: 14 }}>Zona horaria</label>
          <select className="panel-input" value={config.zona_horaria || 'America/Lima'}
            onChange={(e) => setConfig({ ...config, zona_horaria: e.target.value })}>
            <option value="America/Lima">Lima, Perú (America/Lima)</option>
            <option value="America/Bogota">Bogotá (America/Bogota)</option>
            <option value="America/Guayaquil">Quito / Guayaquil (America/Guayaquil)</option>
            <option value="America/La_Paz">La Paz (America/La_Paz)</option>
            <option value="America/Santiago">Santiago (America/Santiago)</option>
            <option value="America/Argentina/Buenos_Aires">Buenos Aires (America/Argentina/Buenos_Aires)</option>
            <option value="America/Mexico_City">Ciudad de México (America/Mexico_City)</option>
            <option value="America/Caracas">Caracas (America/Caracas)</option>
            <option value="UTC">UTC</option>
          </select>
          <p className="panel-texto-ayuda">
            Se usa para mostrar las fechas y horas en la hora local de la instancia.
          </p>
        </div>

        <div className="panel-config-tarjeta" data-tab="marca">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-stamp"></i> Marca de agua de evidencia</span>

          <div className="panel-config-marca-agua-preview">
            <div className="panel-config-marca-agua-foto">
              <div className="panel-config-marca-agua-caja">
                {config.marca_agua_incluye_logo && config.logo && (
                  <img src={config.logo} alt="" className="panel-config-marca-agua-logo" />
                )}
                <div className="panel-config-marca-agua-texto">
                  {config.marca_agua_incluye_fecha_hora && (
                    <div className="panel-config-marca-agua-fila-superior">
                      <strong>{new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false })}</strong>
                      <span className="panel-config-marca-agua-divisor"></span>
                      <span className="panel-config-marca-agua-fecha">
                        {new Date().toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
                        <br />
                        {new Date().toLocaleDateString('es-PE', { weekday: 'short' })}
                      </span>
                    </div>
                  )}
                  {config.marca_agua_incluye_direccion && (
                    <span className="panel-config-marca-agua-linea">Av. Principal 5163, {config.nombre_entidad}</span>
                  )}
                  {config.marca_agua_incluye_clima && <span className="panel-config-marca-agua-linea">23°C · Nublado</span>}
                </div>
              </div>
            </div>
          </div>

          <label className="panel-config-checkbox-row">
            <input type="checkbox" checked={config.marca_agua_incluye_fecha_hora}
              onChange={(e) => setConfig({ ...config, marca_agua_incluye_fecha_hora: e.target.checked })} />
            Incluir fecha y hora
          </label>
          <label className="panel-config-checkbox-row">
            <input type="checkbox" checked={config.marca_agua_incluye_direccion}
              onChange={(e) => setConfig({ ...config, marca_agua_incluye_direccion: e.target.checked })} />
            Incluir dirección
          </label>
          <label className="panel-config-checkbox-row">
            <input type="checkbox" checked={config.marca_agua_incluye_clima}
              onChange={(e) => setConfig({ ...config, marca_agua_incluye_clima: e.target.checked })} />
            Incluir clima
          </label>
          <label className="panel-config-checkbox-row">
            <input type="checkbox" checked={config.marca_agua_incluye_logo}
              disabled={!config.logo && !logoPreview}
              onChange={(e) => setConfig({ ...config, marca_agua_incluye_logo: e.target.checked })} />
            Incluir logo institucional
          </label>
          {!config.logo && !logoPreview && (
            <p className="panel-texto-ayuda">Sube un logo arriba (Identidad) para poder activar esto.</p>
          )}
        </div>

        <div className="panel-config-tarjeta" data-tab="encabezado">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-heading"></i> Encabezado del mapa</span>

          <label className="panel-config-checkbox-row">
            <input type="checkbox" checked={!!config.encabezado_activo}
              onChange={(e) => setConfig({ ...config, encabezado_activo: e.target.checked })} />
            Mostrar encabezado sobre el buscador
          </label>

          <label className="panel-label" style={{ marginTop: 14 }}>Título</label>
          <input className="panel-input" maxLength={150} placeholder="Ej. Reporta tu incidencia"
            value={config.encabezado_titulo || ''}
            onChange={(e) => setConfig({ ...config, encabezado_titulo: e.target.value })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Subtítulo (opcional)</label>
          <input className="panel-input" maxLength={220} placeholder="Ej. Ayúdanos a mejorar tu distrito"
            value={config.encabezado_subtitulo || ''}
            onChange={(e) => setConfig({ ...config, encabezado_subtitulo: e.target.value })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Mensajes que rotan (uno por línea)</label>
          <textarea className="panel-input" rows={4}
            placeholder={'Ej.\nTu voz en el mapa\nCada reporte cuenta\nJuntos mejoramos el distrito'}
            value={config.encabezado_mensajes || ''}
            onChange={(e) => setConfig({ ...config, encabezado_mensajes: e.target.value })} />
          <p className="panel-texto-ayuda">
            Si escribes mensajes aquí, el encabezado los rota con un efecto tipo PowerPoint
            (en vez del título/subtítulo corrido).
          </p>

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={!!config.encabezado_gradiente}
              onChange={(e) => setConfig({ ...config, encabezado_gradiente: e.target.checked })} />
            Fondo con degradado (2 colores)
          </label>

          <label className="panel-label" style={{ marginTop: 14 }}>
            {config.encabezado_gradiente ? 'Color de fondo (inicio)' : 'Color de fondo'}
          </label>
          <SelectorColor valor={config.encabezado_color_fondo || '#FFFFFF'}
            onCambiar={(v) => setConfig({ ...config, encabezado_color_fondo: v })} />

          {config.encabezado_gradiente && (
            <>
              <label className="panel-label" style={{ marginTop: 14 }}>Color de fondo (fin del degradado)</label>
              <SelectorColor valor={config.encabezado_color_fondo_2 || '#FFFFFF'}
                onCambiar={(v) => setConfig({ ...config, encabezado_color_fondo_2: v })} />
            </>
          )}

          <label className="panel-label" style={{ marginTop: 14 }}>Color del título</label>
          <SelectorColor valor={config.encabezado_color_texto || '#14161A'}
            onCambiar={(v) => setConfig({ ...config, encabezado_color_texto: v })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Color del subtítulo</label>
          <SelectorColor valor={config.encabezado_color_subtitulo || '#14161A'}
            onCambiar={(v) => setConfig({ ...config, encabezado_color_subtitulo: v })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Imagen (opcional)</label>
          <div className="panel-config-banner-preview-caja">
            {(encabezadoPreview || config.encabezado_imagen)
              ? <img src={encabezadoPreview || config.encabezado_imagen} alt="Encabezado actual" />
              : <span className="panel-texto-ayuda">Sin imagen — se muestra solo el texto</span>}
          </div>
          <div className="panel-gu-acciones" style={{ marginTop: 8 }}>
            <label className="panel-boton-secundario panel-gu-label-archivo">
              {(encabezadoPreview || config.encabezado_imagen) ? 'Cambiar imagen' : 'Subir imagen'}
              <input type="file" accept="image/png,image/jpeg" onChange={handleEncabezadoImagen} hidden />
            </label>
            {(encabezadoPreview || config.encabezado_imagen) && (
              <button type="button" className="panel-boton-secundario" onClick={quitarEncabezadoImagen}>Quitar imagen</button>
            )}
          </div>
          <p className="panel-texto-ayuda">
            Aparece arriba del buscador en el mapa ciudadano, para que se entienda de qué trata la app.
            Con imagen se muestra como banner; sin imagen, como un cuadro con el título y el subtítulo.
          </p>
        </div>

        <div className="panel-config-tarjeta" data-tab="inicio">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-mobile-screen"></i> Pantalla de inicio</span>

          <label className="panel-label">Mensaje</label>
          <input className="panel-input" maxLength={220} placeholder="Ej. Reporta incidencias de tu zona en minutos"
            value={config.inicio_mensaje || ''}
            onChange={(e) => setConfig({ ...config, inicio_mensaje: e.target.value })} />

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={config.inicio_auto !== false}
              onChange={(e) => setConfig({ ...config, inicio_auto: e.target.checked })} />
            Ingreso automático (sin botón)
          </label>
          <p className="panel-texto-ayuda">
            {config.inicio_auto !== false
              ? 'Entra solo al mapa después de unos segundos.'
              : 'Muestra un botón "Continuar" y espera a que el ciudadano lo pulse.'}
          </p>

          <label className="panel-label" style={{ marginTop: 14 }}>Color de fondo</label>
          <SelectorColor valor={config.inicio_color_fondo || '#14161A'}
            onCambiar={(v) => setConfig({ ...config, inicio_color_fondo: v })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Color del texto</label>
          <SelectorColor valor={config.inicio_color_texto || '#FFFFFF'}
            onCambiar={(v) => setConfig({ ...config, inicio_color_texto: v })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Botón "Continuar" — texto</label>
          <input className="panel-input" maxLength={40} placeholder="Ej. Continuar"
            value={config.inicio_boton_texto || ''}
            onChange={(e) => setConfig({ ...config, inicio_boton_texto: e.target.value })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Botón "Continuar" — color de fondo</label>
          <SelectorColor valor={config.inicio_boton_color || '#1B5FA8'}
            onCambiar={(v) => setConfig({ ...config, inicio_boton_color: v })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Botón "Continuar" — color del texto</label>
          <SelectorColor valor={config.inicio_boton_color_texto || '#FFFFFF'}
            onCambiar={(v) => setConfig({ ...config, inicio_boton_color_texto: v })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Botón "Continuar" — forma</label>
          <select className="panel-input" value={config.inicio_boton_forma || 'pill'}
            onChange={(e) => setConfig({ ...config, inicio_boton_forma: e.target.value })}>
            <option value="pill">Pastilla (muy redondeado)</option>
            <option value="rounded">Rectángulo redondeado</option>
            <option value="square">Cuadrado (poco redondeado)</option>
          </select>

          <label className="panel-label" style={{ marginTop: 14 }}>Imagen de inicio (opcional)</label>
          <div className="panel-config-banner-preview-caja">
            {(inicioPreview || config.inicio_imagen)
              ? <img src={inicioPreview || config.inicio_imagen} alt="Imagen de inicio" />
              : <span className="panel-texto-ayuda">Sin imagen — se usa el logo de la entidad</span>}
          </div>
          <div className="panel-gu-acciones" style={{ marginTop: 8 }}>
            <label className="panel-boton-secundario panel-gu-label-archivo">
              {(inicioPreview || config.inicio_imagen) ? 'Cambiar imagen' : 'Subir imagen'}
              <input type="file" accept="image/png,image/jpeg" onChange={handleInicioImagen} hidden />
            </label>
            {(inicioPreview || config.inicio_imagen) && (
              <button type="button" className="panel-boton-secundario" onClick={quitarInicioImagen}>Quitar imagen</button>
            )}
          </div>

          <label className="panel-config-checkbox-row" style={{ marginTop: 14 }}>
            <input type="checkbox" checked={!!config.inicio_fondo_imagen_activo}
              onChange={(e) => setConfig({ ...config, inicio_fondo_imagen_activo: e.target.checked })} />
            Usar imagen de fondo
          </label>

          <label className="panel-label" style={{ marginTop: 14 }}>Imagen de fondo (opcional)</label>
          <div className="panel-config-banner-preview-caja">
            {(inicioFondoPreview || config.inicio_fondo_imagen)
              ? <img src={inicioFondoPreview || config.inicio_fondo_imagen} alt="Fondo de inicio" />
              : <span className="panel-texto-ayuda">Sin imagen de fondo</span>}
          </div>
          <div className="panel-gu-acciones" style={{ marginTop: 8 }}>
            <label className="panel-boton-secundario panel-gu-label-archivo">
              {(inicioFondoPreview || config.inicio_fondo_imagen) ? 'Cambiar fondo' : 'Subir fondo'}
              <input type="file" accept="image/png,image/jpeg" onChange={handleInicioFondoImagen} hidden />
            </label>
            {(inicioFondoPreview || config.inicio_fondo_imagen) && (
              <button type="button" className="panel-boton-secundario" onClick={quitarInicioFondoImagen}>Quitar fondo</button>
            )}
          </div>
          <p className="panel-texto-ayuda">
            Con "Usar imagen de fondo" activado, la imagen cubre toda la pantalla de inicio
            (encima del color de fondo). Apagado, se ve el color de fondo.
          </p>

          <label className="panel-label" style={{ marginTop: 14 }}>Imagen que reemplaza los 3 íconos (opcional)</label>
          <div className="panel-config-banner-preview-caja">
            {(featuresPreview || config.inicio_features_imagen)
              ? <img src={featuresPreview || config.inicio_features_imagen} alt="Íconos del inicio" />
              : <span className="panel-texto-ayuda">Sin imagen — se muestran los 3 íconos (Seguro/Rápido/Confiable)</span>}
          </div>
          <div className="panel-gu-acciones" style={{ marginTop: 8 }}>
            <label className="panel-boton-secundario panel-gu-label-archivo">
              {(featuresPreview || config.inicio_features_imagen) ? 'Cambiar imagen' : 'Subir imagen'}
              <input type="file" accept="image/png,image/jpeg" onChange={handleFeaturesImagen} hidden />
            </label>
            {(featuresPreview || config.inicio_features_imagen) && (
              <button type="button" className="panel-boton-secundario" onClick={quitarFeaturesImagen}>Quitar imagen</button>
            )}
          </div>
        </div>

        <div className="panel-config-tarjeta" data-tab="reporte">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-pen"></i> Textos del formulario de reporte</span>
          <p className="panel-texto-ayuda">
            Personaliza el título, la descripción y el color de cada paso. En blanco = se usa el texto por defecto.
          </p>
          {PASOS_REPORTE.map(({ clave, etiqueta, titulo: td, subtitulo: sd }) => {
            const paso = (config.reporte_textos || {})[clave] || {};
            const setPaso = (cambios) => setConfig({
              ...config,
              reporte_textos: { ...(config.reporte_textos || {}), [clave]: { ...paso, ...cambios } },
            });
            return (
              <div key={clave} style={{ marginTop: 14 }}>
                <label className="panel-label">{etiqueta}</label>
                <input className="panel-input" maxLength={120} placeholder={td || 'Título'}
                  value={paso.titulo || ''} onChange={(e) => setPaso({ titulo: e.target.value })} />
                <input className="panel-input" style={{ marginTop: 6 }} maxLength={220} placeholder={sd || 'Descripción (opcional)'}
                  value={paso.subtitulo || ''} onChange={(e) => setPaso({ subtitulo: e.target.value })} />
                <div style={{ marginTop: 6 }}>
                  <SelectorColor valor={paso.color || '#14161A'} onCambiar={(v) => setPaso({ color: v })} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="panel-config-tarjeta" data-tab="comentarios">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-comments"></i> Comentarios en los puntos</span>

          <label className="panel-config-checkbox-row">
            <input type="checkbox" checked={config.comentarios_activos !== false}
              onChange={(e) => setConfig({ ...config, comentarios_activos: e.target.checked })} />
            Activar comentarios en los puntos ya registrados
          </label>

          <label className="panel-config-checkbox-row" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={!!config.comentarios_requieren_aprobacion}
              onChange={(e) => setConfig({ ...config, comentarios_requieren_aprobacion: e.target.checked })} />
            Requerir aprobación del admin antes de publicar
          </label>
          <p className="panel-texto-ayuda">
            Sin marcar (por defecto), los comentarios se publican al instante y el admin puede
            ocultarlos/bloquearlos después. Marcado, quedan pendientes hasta que los aprueben.
          </p>

          <label className="panel-label" style={{ marginTop: 14 }}>Minutos para editar/eliminar el propio comentario</label>
          <input className="panel-input" type="number" min="1" max="1440"
            value={config.comentarios_minutos_edicion ?? 15}
            onChange={(e) => setConfig({ ...config, comentarios_minutos_edicion: parseInt(e.target.value, 10) || 15 })} />
        </div>

        <div className="panel-config-tarjeta" data-tab="politicas">
          <span className="panel-config-tarjeta-titulo"><i className="fas fa-file-shield"></i> Políticas de uso y datos</span>

          <label className="panel-config-checkbox-row">
            <input type="checkbox" checked={!!config.politicas_activas}
              onChange={(e) => setConfig({ ...config, politicas_activas: e.target.checked })} />
            Exigir aceptar las políticas antes de entrar
          </label>
          <p className="panel-texto-ayuda">
            Si está activo, el ciudadano ve el texto y debe marcar "He leído y acepto" antes de
            usar el mapa. El enlace del splash permite volver a leerlas en cualquier momento.
          </p>

          <label className="panel-label" style={{ marginTop: 14 }}>Título</label>
          <input className="panel-input" maxLength={150}
            value={config.politicas_titulo || ''}
            onChange={(e) => setConfig({ ...config, politicas_titulo: e.target.value })} />

          <label className="panel-label" style={{ marginTop: 14 }}>Versión</label>
          <input className="panel-input" style={{ maxWidth: 140 }} maxLength={20}
            value={config.politicas_version || ''}
            onChange={(e) => setConfig({ ...config, politicas_version: e.target.value })} />
          <p className="panel-texto-ayuda">
            Si cambias el texto, sube la versión (ej. 1.1) para volver a pedir la aceptación a todos.
          </p>

          <label className="panel-label" style={{ marginTop: 14 }}>Texto de las políticas</label>
          <textarea className="panel-input" rows={14}
            value={config.politicas_contenido || ''}
            onChange={(e) => setConfig({ ...config, politicas_contenido: e.target.value })} />

          <label className="panel-config-checkbox-row" style={{ marginTop: 18 }}>
            <input type="checkbox" checked={!!config.bloqueo_ip_activo}
              onChange={(e) => setConfig({ ...config, bloqueo_ip_activo: e.target.checked })} />
            Bloquear por IP a quien viole las políticas de uso
          </label>
          <p className="panel-texto-ayuda">
            Al activarlo, las IPs de la lista <b>Bloqueos IP</b> (menú Administración) reciben 403.
            Se guardan datos básicos: IP, motivo y fecha.
          </p>
        </div>
      </form>
    </PanelShell>
  );
};

export default ConfiguracionInstancia;
